import React from 'react';
import { Heart } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import teamPhoto from '../assets/team/MarieMorgane2.jpg';
import centreGeneuille from '../assets/centre_geneuille.jpeg';
import { useScrollReveal } from '../hooks/useScrollReveal';
import FaqCTA from '../components/ui/FaqCTA';

interface ValueCardProps {
  title: string;
  description: string;
  index: number;
}

const ValueCard: React.FC<ValueCardProps> = ({ title, description, index }) => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });

  return (
    <div 
      ref={ref}
      style={{ transitionDelay: `${index * 0.15}s` }}
      className={`bg-white p-6 rounded-2xl shadow-soft hover:shadow-lg transition-all duration-700 ease-out hover:scale-105 hover:-translate-y-2 group transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
    >
      <h3 className="font-playfair text-xl font-bold text-neutral-ink mb-4 group-hover:text-brand transition-colors duration-300">
        {title}
      </h3>
      <p className="font-nunito text-neutral-body">
        {description}
      </p>
      <div className="mt-4 h-1 w-0 bg-gradient-to-r from-accent-rose to-brand group-hover:w-full transition-all duration-500"></div>
    </div>
  );
};

const About: React.FC = () => {
  const { ref: heroRef, isVisible: heroVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });
  const { ref: imageRef, isVisible: imageVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });
  const { ref: card1Ref, isVisible: card1Visible } = useScrollReveal<HTMLDivElement>({ threshold: 0.15 });
  const { ref: card2Ref, isVisible: card2Visible } = useScrollReveal<HTMLDivElement>({ threshold: 0.15 });
  const { ref: centreRef, isVisible: centreVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });
  const { ref: centreImageRef, isVisible: centreImageVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });
  const { ref: valeursRef, isVisible: valeursVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });

  return (
    <article className="pt-24 pb-16 bg-neutral-offwhite">
      <SEOHead
        title="Notre équipe — Marie Amet & Morgane Darbon"
        description="Rencontrez Marie Amet et Morgane Darbon, praticiennes bien-être périnatal à Doux ce Moman — Geneuille, Doubs."
        path="/a-propos"
      />
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <header 
          ref={heroRef}
          className={`text-center mb-16 transition-all duration-700 ease-out transform ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-neutral-ink mb-6">
            Notre Histoire
          </h1>
        </header>

        {/* Main Content */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
          <div 
            ref={imageRef}
            className={`relative transition-all duration-700 ease-out transform ${imageVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <div className="rounded-2xl overflow-hidden shadow-lg transition-all duration-500 hover:shadow-2xl hover:scale-105">
              <img
                src={teamPhoto}
                alt="Marie et Morgane - Notre équipe"
                loading="lazy"
                className="w-full h-[400px] object-cover transition-transform duration-700 hover:scale-110"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-accent-rose rounded-full opacity-40 blur-md animate-pulse"></div>
          </div>
          
          <div className="flex flex-col gap-6">
            <div 
              ref={card1Ref}
              className={`bg-white p-8 rounded-2xl shadow-soft hover:shadow-lg transition-all duration-700 ease-out hover:scale-105 hover:-translate-y-1 group transform ${card1Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: '0.1s' }}
            >
              <h2 className="font-playfair text-2xl md:text-3xl font-bold text-neutral-ink mb-4 group-hover:text-brand transition-colors duration-300">
                Notre rencontre
              </h2>
              <p className="font-nunito text-neutral-body leading-relaxed">
                Il y a deux ans, nos chemins se sont croisés dans le cadre de notre profession d'infirmières. Au-delà de notre travail, nous partagions une intuition forte : le désir de créer ensemble un projet qui nous ressemble vraiment. C'est au fil de nos discussions, notamment autour de notre propre maternité, que nous avons réalisé à quel point la découverte de ce nouveau rôle - avec ses joies, mais aussi ses défis - nécessitait un soutien bienveillant. C'est là qu'a germé l'idée de Doux ce Moman.
              </p>
              <div className="mt-4 h-1 w-0 bg-gradient-to-r from-accent-rose to-brand group-hover:w-full transition-all duration-500"></div>
            </div>

            <div 
              ref={card2Ref}
              className={`bg-white p-8 rounded-2xl shadow-soft hover:shadow-lg transition-all duration-700 ease-out hover:scale-105 hover:-translate-y-1 group transform ${card2Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: '0.2s' }}
            >
              <h2 className="font-playfair text-2xl md:text-3xl font-bold text-neutral-ink mb-4 group-hover:text-brand transition-colors duration-300">
                Le Rêve devient Réalité
              </h2>
              <p className="font-nunito text-neutral-body leading-relaxed">
                Après des mois de réflexion intense, de formations ciblées et la recherche d'un local qui respire la sérénité, notre projet a pris forme. Notre ambition, elle, est simple : offrir un cadre apaisant, un véritable havre de paix où vous pouvez vous ressourcer, échanger et prendre soin de vous, car ces moments de vie méritent toute la douceur du monde. Doux ce Moman est né de notre volonté de vous accompagner avec cœur et professionnalisme.
              </p>
              <div className="mt-4 h-1 w-0 bg-gradient-to-r from-accent-rose to-brand group-hover:w-full transition-all duration-500"></div>
            </div>
          </div>
        </section>

        {/* Notre Centre */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
          <div 
            ref={centreRef}
            className={`bg-white p-8 rounded-2xl shadow-soft hover:shadow-lg transition-all duration-700 ease-out hover:scale-105 hover:-translate-y-1 group transform ${centreVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <h2 className="font-playfair text-2xl md:text-3xl font-bold text-neutral-ink mb-4 group-hover:text-brand transition-colors duration-300">
              Notre Centre à Geneuille
            </h2>
            <p className="font-nunito text-neutral-body mb-6 leading-relaxed">
            Situé dans le charmant village de Geneuille, dans le Doubs, notre centre est un véritable havre de paix. Nous avons créé un environnement chaleureux et apaisant, où chaque détail a été pensé pour le confort et le bien-être de nos clients. 
            Idéalement placé, le centre se trouve à seulement 15 minutes de Besançon.
            </p>
            <div className="flex items-center space-x-4 text-neutral-body group">
              <Heart className="text-brand group-hover:scale-110 transition-transform duration-300" size={24} />
              <span className="font-nunito">Geneuille, Doubs (25)</span>
            </div>
            <div className="mt-4 h-1 w-0 bg-gradient-to-r from-accent-rose to-brand group-hover:w-full transition-all duration-500"></div>
          </div>
          
          <div 
            ref={centreImageRef}
            className={`relative transition-all duration-700 ease-out transform ${centreImageVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <div className="rounded-2xl overflow-hidden shadow-lg transition-all duration-500 hover:shadow-2xl hover:scale-105">
              <img
                src={centreGeneuille}
                alt="Notre centre à Geneuille"
                loading="lazy"
                className="w-full h-[300px] object-cover transition-transform duration-700 hover:scale-110"
              />
            </div>
          </div>
        </section>

        {/* Nos Valeurs */}
        <section className="text-center mb-12">
          <h2 
            ref={valeursRef}
            className={`font-playfair text-2xl md:text-3xl font-bold text-neutral-ink mb-12 transition-all duration-700 ease-out transform ${valeursVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            Nos Valeurs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Professionnalisme",
                description: "Nous sommes formées à toutes nos pratiques et veillons sans cesse à enrichir nos connaissances. C'est notre garantie pour un accompagnement fiable et de qualité"
              },
              {
                title: "Bienveillance",
                description: "Nous vous accueillons dans un environnement chaleureux et sans jugement. Notre bienveillance assure que vous vous sentez écoutée, comprise et profondément soutenue à chaque étape."
              },
              {
                title: "Écoute",
                description: "Nous vous accordons une écoute profonde et non-jugeante. Chaque accompagnement est entièrement personnalisé et respecte votre parcours et vos convictions profondes"
              }
            ].map((value, index) => (
              <ValueCard key={index} title={value.title} description={value.description} index={index} />
            ))}
          </div>
        </section>

        {/* FAQ CTA */}
        <FaqCTA />
      </div>
    </article>
  );
};

export default About;